import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { ClipboardCopy, Download, Printer, Shirt } from "lucide-react";
import { toast } from "sonner";

import { AppShell } from "@/components/app/app-shell";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { NO_SHIRT_OPTION, SHIRT_SIZES } from "@/lib/claim-fields";
import { adminShirtClaimsQuery } from "@/lib/queries";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_authenticated/t1dwpanel/shirts")({
  head: () => ({
    meta: [
      { title: "Shirt Orders | Warrior Volunteer" },
      { name: "description", content: "T-shirt claims by event, size, and status." },
      { property: "og:title", content: "Shirt Orders | Warrior Volunteer" },
      { property: "og:description", content: "T-shirt claims by event, size, and status." },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: ShirtOrdersPage,
});

function formatDate(value: string | null) {
  if (!value) return "Date TBD";
  return new Date(`${value}T12:00:00`).toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

function ShirtOrdersPage() {
  const { data: claims = [] } = useQuery(adminShirtClaimsQuery);
  const [eventFilter, setEventFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("confirmed");

  const events = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of claims) if (c.event_id) map.set(c.event_id, c.event_title);
    return Array.from(map.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [claims]);

  const filtered = useMemo(
    () =>
      claims.filter(
        (c) =>
          (eventFilter === "all" || c.event_id === eventFilter) &&
          (statusFilter === "all" || c.status === statusFilter),
      ),
    [claims, eventFilter, statusFilter],
  );

  // Order planning: confirmed helpers who still need a shirt, counted per
  // event + size with quantity applied.
  const orderSummary = useMemo(() => {
    const confirmed = filtered.filter(
      (c) => c.status === "confirmed" && c.size !== NO_SHIRT_OPTION,
    );
    const perEvent = new Map<
      string,
      { title: string; date: string | null; sizes: Map<string, number>; total: number }
    >();
    for (const c of confirmed) {
      const key = c.event_id ?? "unknown";
      const entry =
        perEvent.get(key) ??
        { title: c.event_title, date: c.event_date, sizes: new Map<string, number>(), total: 0 };
      entry.sizes.set(c.size, (entry.sizes.get(c.size) ?? 0) + c.quantity);
      entry.total += c.quantity;
      perEvent.set(key, entry);
    }
    return Array.from(perEvent.values()).sort((a, b) =>
      (a.date ?? "9999").localeCompare(b.date ?? "9999"),
    );
  }, [filtered]);

  const grandTotal = orderSummary.reduce((sum, e) => sum + e.total, 0);

  // ---- Vendor order form ----
  const [orderEvent, setOrderEvent] = useState("all");
  const [extras, setExtras] = useState<Record<string, number>>({});
  const [orderNote, setOrderNote] = useState("");

  const orderLines = useMemo(() => {
    const counts = new Map<string, number>(SHIRT_SIZES.map((s) => [s, 0]));
    for (const c of claims) {
      if (c.status !== "confirmed" || c.size === NO_SHIRT_OPTION) continue;
      if (orderEvent !== "all" && c.event_id !== orderEvent) continue;
      counts.set(c.size, (counts.get(c.size) ?? 0) + c.quantity);
    }
    return SHIRT_SIZES.map((size) => {
      const claimed = counts.get(size) ?? 0;
      const extra = Math.max(0, Math.floor(extras[size] ?? 0));
      return { size, claimed, extra, total: claimed + extra };
    }).filter((l) => l.claimed > 0 || l.extra > 0);
  }, [claims, orderEvent, extras]);

  const orderTotal = orderLines.reduce((sum, l) => sum + l.total, 0);
  const orderEventTitle =
    orderEvent === "all"
      ? "All events"
      : (events.find(([id]) => id === orderEvent)?.[1] ?? "Selected event");

  const orderText = useMemo(() => {
    const lines = [
      "T1D Warriors - Shirt Order",
      `Scope: ${orderEventTitle}`,
      `Generated: ${new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}`,
      "",
      "Size    Needed    Extra    Order",
      ...orderLines.map(
        (l) =>
          `${l.size.padEnd(7)} ${String(l.claimed).padEnd(9)} ${String(l.extra).padEnd(8)} ${l.total}`,
      ),
      "",
      `Total shirts: ${orderTotal}`,
    ];
    if (orderNote.trim()) lines.push("", `Notes: ${orderNote.trim()}`);
    return lines.join("\n");
  }, [orderLines, orderTotal, orderEventTitle, orderNote]);

  const copyOrder = async () => {
    try {
      await navigator.clipboard.writeText(orderText);
      toast.success("Order copied. Paste it into an email to the vendor.");
    } catch {
      toast.error("Could not copy. Select and copy the text manually.");
    }
  };

  const downloadOrder = () => {
    const blob = new Blob([orderText], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `shirt-order-${orderEvent === "all" ? "all-events" : orderEventTitle.toLowerCase().replace(/[^a-z0-9]+/g, "-")}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const printOrder = () => {
    const win = window.open("", "_blank", "width=640,height=720");
    if (!win) return;
    win.document.write(
      `<html><head><title>Shirt order</title><style>body{font-family:ui-monospace,monospace;padding:32px}pre{font-size:14px;line-height:1.6}</style></head><body><pre>${orderText
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")}</pre></body></html>`,
    );
    win.document.close();
    win.print();
  };

  return (
    <AppShell
      title="Shirt orders"
      eyebrow="Admin"
      actions={
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Shirt className="h-4 w-4" />
          {grandTotal} shirt{grandTotal === 1 ? "" : "s"} to order
        </div>
      }
    >
      <div className="space-y-6">
        <div className="flex flex-wrap gap-2">
          <Select value={eventFilter} onValueChange={setEventFilter}>
            <SelectTrigger className="w-64">
              <SelectValue placeholder="All events" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All events</SelectItem>
              {events.map(([id, title]) => (
                <SelectItem key={id} value={id}>
                  {title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-44">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="confirmed">Confirmed</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
              <SelectItem value="all">All statuses</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <Card>
          <CardHeader className="flex-row items-center justify-between gap-2 space-y-0">
            <CardTitle className="text-base">Vendor order form</CardTitle>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={copyOrder} disabled={orderLines.length === 0}>
                <ClipboardCopy className="h-4 w-4" />
                Copy
              </Button>
              <Button size="sm" variant="outline" onClick={downloadOrder} disabled={orderLines.length === 0}>
                <Download className="h-4 w-4" />
                Download
              </Button>
              <Button size="sm" variant="outline" onClick={printOrder} disabled={orderLines.length === 0}>
                <Printer className="h-4 w-4" />
                Print
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Builds a per-size order list from confirmed claims, ready to email to the vendor.
              Add extras per size to cover walk-ups or sizing swaps.
            </p>
            <div className="flex flex-wrap items-end gap-2">
              <div className="grid gap-1.5">
                <Label htmlFor="order-event">Scope</Label>
                <Select value={orderEvent} onValueChange={setOrderEvent}>
                  <SelectTrigger id="order-event" className="w-64">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All events</SelectItem>
                    {events.map(([id, title]) => (
                      <SelectItem key={id} value={id}>
                        {title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid min-w-56 flex-1 gap-1.5">
                <Label htmlFor="order-note">Order note (optional)</Label>
                <Input
                  id="order-note"
                  value={orderNote}
                  onChange={(e) => setOrderNote(e.target.value)}
                  placeholder="e.g. Needed by Sept 20, navy tees with white print"
                />
              </div>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Size</TableHead>
                  <TableHead>Needed</TableHead>
                  <TableHead className="w-28">Extra</TableHead>
                  <TableHead>Order</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {SHIRT_SIZES.map((size) => {
                  const line = orderLines.find((l) => l.size === size);
                  const claimed = line?.claimed ?? 0;
                  const extra = extras[size] ?? 0;
                  return (
                    <TableRow key={size}>
                      <TableCell className="font-medium">{size}</TableCell>
                      <TableCell>{claimed}</TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          min={0}
                          inputMode="numeric"
                          className="h-8 w-20"
                          value={extras[size] ?? 0}
                          onChange={(e) =>
                            setExtras((prev) => ({
                              ...prev,
                              [size]: Math.max(0, Math.floor(Number(e.target.value) || 0)),
                            }))
                          }
                          aria-label={`Extra ${size} shirts`}
                        />
                      </TableCell>
                      <TableCell>
                        <Badge variant={claimed + extra > 0 ? "secondary" : "outline"}>
                          {claimed + extra}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  );
                })}
                <TableRow>
                  <TableCell className="font-semibold">Total</TableCell>
                  <TableCell className="font-semibold">
                    {orderLines.reduce((s, l) => s + l.claimed, 0)}
                  </TableCell>
                  <TableCell />
                  <TableCell className="font-semibold">{orderTotal}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
            {orderLines.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                Nothing to order for this scope yet. Confirmed claims that need a shirt will
                show up here automatically.
              </p>
            ) : (
              <pre className="overflow-x-auto rounded-md border border-border bg-muted/50 p-3 text-xs leading-relaxed">
                {orderText}
              </pre>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Shirts to order by event</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {orderSummary.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No confirmed shirt claims for this selection.
              </p>
            ) : (
              orderSummary.map((event) => (
                <div
                  key={`${event.title}-${event.date ?? "tbd"}`}
                  className="rounded-md border border-border p-3"
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className="truncate font-medium">{event.title}</p>
                      <p className="text-xs text-muted-foreground">{formatDate(event.date)}</p>
                    </div>
                    <Badge variant="secondary">{event.total} total</Badge>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {Array.from(event.sizes.entries())
                      .sort((a, b) => a[0].localeCompare(b[0]))
                      .map(([size, count]) => (
                        <Badge key={size} variant="outline">
                          {size}: {count}
                        </Badge>
                      ))}
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Shirt claim log</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Helper</TableHead>
                  <TableHead>Event</TableHead>
                  <TableHead>Spot</TableHead>
                  <TableHead>Size</TableHead>
                  <TableHead>Qty</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Claimed</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-muted-foreground">
                      No shirt claims match these filters.
                    </TableCell>
                  </TableRow>
                ) : (
                  filtered.map((c) => (
                    <TableRow key={c.id}>
                      <TableCell>
                        <div className="font-medium">{c.full_name}</div>
                        <div className="text-xs text-muted-foreground">{c.email}</div>
                      </TableCell>
                      <TableCell>
                        <div>{c.event_title}</div>
                        <div className="text-xs text-muted-foreground">
                          {formatDate(c.event_date)}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">{c.slot_title}</TableCell>
                      <TableCell>
                        <Badge
                          variant={c.size === NO_SHIRT_OPTION ? "outline" : "secondary"}
                          className={cn(c.size === NO_SHIRT_OPTION && "text-muted-foreground")}
                        >
                          {c.size === NO_SHIRT_OPTION ? "Has a shirt" : c.size}
                        </Badge>
                      </TableCell>
                      <TableCell>{c.quantity}</TableCell>
                      <TableCell>
                        <Badge variant={c.status === "confirmed" ? "default" : "destructive"}>
                          {c.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {new Date(c.claimed_at).toLocaleString("en-US", {
                          month: "short",
                          day: "numeric",
                          hour: "numeric",
                          minute: "2-digit",
                        })}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </AppShell>
  );
}
